// receiptService.js
import baseURL from '../baseURL';
import { fingerprintService } from './fingerprintService';

class ReceiptService {
  constructor() {
    this.baseUrl = `${baseURL}/receipts`;
  }

  /**
   * Search for pledges by scanning fingerprint.
   * Captures on client PC (local bridge http://localhost:5001) then sends template to server.
   * @param {number} threshold - Matching threshold (default: 60)
   * @returns {Promise<Object>} - Object containing scanned fingerprint info and matched pledges
   */
  async searchPledgeByFingerprint(threshold = 60) {
    try {
      const captured = await fingerprintService.capture();
      const template = captured?.template;
      if (!template) {
        throw new Error('No fingerprint. Start the local bridge (run-secugen-http-server.bat) on this PC.');
      }
      const response = await fetch(`${this.baseUrl}/search-pledge-by-fingerprint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ template, threshold })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to search pledges by fingerprint');
      }

      return data?.data || data;
    } catch (error) {
      console.error('Error searching pledges by fingerprint:', error);
      throw new Error(error.message || 'Failed to search pledges by fingerprint. Please try again.');
    }
  }

  /**
   * Create a new receipt
   * @param {Object} receiptData - Receipt data
   * @returns {Promise<Object>}
   */
  async createReceipt(receiptData) {
    try {
      const response = await fetch(`${this.baseUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(receiptData)
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to create receipt');
      }

      return data;
    } catch (error) {
      console.error('Error creating receipt:', error);
      throw new Error(error.message || 'Failed to create receipt');
    }
  }

  /**
   * Get receipts by ticket number
   * @param {string} ticketNo - Ticket number
   * @returns {Promise<Array>}
   */
  async getReceiptsByTicketNo(ticketNo) {
    try {
      const response = await fetch(`${this.baseUrl}?ticketNo=${encodeURIComponent(ticketNo)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to fetch receipts');
      }

      return data?.data || [];
    } catch (error) {
      console.error('Error fetching receipts:', error);
      throw new Error(error.message || 'Failed to fetch receipts');
    }
  }

  /**
   * Get all receipts
   * @returns {Promise<Array>}
   */
  async getAllReceipts() {
    try {
      const response = await fetch(`${this.baseUrl}/receipts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to fetch receipts');
      }

      return data?.data || [];
    } catch (error) {
      console.error('Error fetching receipts:', error);
      throw new Error(error.message || 'Failed to fetch receipts');
    }
  }

  /**
   * Get single receipt by ID
   * @param {string} id - Receipt ID
   * @returns {Promise<Object>}
   */
  async getReceiptById(id) {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to fetch receipt');
      }

      return data?.data || null;
    } catch (error) {
      console.error('Error fetching receipt:', error);
      throw new Error(error.message || 'Failed to fetch receipt');
    }
  }

  /**
   * Update receipt
   * @param {string} id - Receipt ID
   * @param {Object} updateData - Updated receipt data
   * @returns {Promise<Object>}
   */
  async updateReceipt(id, updateData) {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to update receipt');
      }

      return data;
    } catch (error) {
      console.error('Error updating receipt:', error);
      throw new Error(error.message || 'Failed to update receipt');
    }
  }

  /**
   * Delete receipt
   * @param {string} id - Receipt ID
   * @returns {Promise<Object>}
   */
  async deleteReceipt(id) {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to delete receipt');
      }

      return data;
    } catch (error) {
      console.error('Error deleting receipt:', error);
      throw new Error(error.message || 'Failed to delete receipt');
    }
  }
}

// Export a singleton instance
export const receiptService = new ReceiptService();
export default receiptService;
